import React, { useEffect, useState, useCallback } from "react";
import { Crown, Trophy, Award } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Top-Gifter Leaderboard side panel for the live room. Fetches the
 * top-N gifters from `/api/live/{room}/leaderboard` on mount, then
 * refetches whenever the parent signals a new gift arrived (via the
 * `refreshTrigger` prop — a counter that increments on each incoming
 * gift data-packet). A background 25-second poll acts as a safety net
 * in case a data-packet is dropped.
 *
 * Design: compact vertical stack. #1 gets a gold crown + full-bleed
 * gradient, #2 silver, #3 bronze. Anything below is a plain row. The
 * whole panel is `pointer-events-auto` so users can tap a name to
 * (eventually) visit the sender's profile.
 */

const RANK_STYLE = [
  {
    Icon: Crown,
    ring: "border-[#FFD93D]",
    text: "text-[#FFD93D]",
    glow: "shadow-[0_0_20px_rgba(255,217,61,0.4)]",
    grad: "from-[#FFD93D]/25 via-[#FFD93D]/10 to-transparent",
  },
  {
    Icon: Trophy,
    ring: "border-white/80",
    text: "text-white/90",
    glow: "shadow-[0_0_16px_rgba(255,255,255,0.25)]",
    grad: "from-white/15 via-white/5 to-transparent",
  },
  {
    Icon: Award,
    ring: "border-[#FF9F1C]",
    text: "text-[#FF9F1C]",
    glow: "shadow-[0_0_14px_rgba(255,159,28,0.4)]",
    grad: "from-[#FF9F1C]/20 via-[#FF9F1C]/5 to-transparent",
  },
];

export default function LiveLeaderboard({ roomName, refreshTrigger = 0 }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const pull = useCallback(async () => {
    if (!roomName) return;
    try {
      const { data } = await api.get(`/live/${roomName}/leaderboard?limit=5`);
      setEntries(data.leaderboard || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [roomName]);

  // Fetch on mount + every 25s + when a gift lands.
  useEffect(() => {
    pull();
    const id = setInterval(pull, 25000);
    return () => clearInterval(id);
  }, [pull]);

  useEffect(() => {
    if (refreshTrigger > 0) pull();
  }, [refreshTrigger, pull]);

  if (loading) return null;
  if (entries.length === 0) return null;

  return (
    <div
      className="absolute top-16 left-3 z-20 w-52 max-w-[45vw] bg-black/70 backdrop-blur-md border border-[#FFD93D]/40 p-2 space-y-1.5 pointer-events-auto shadow-[0_0_30px_rgba(255,217,61,0.15)]"
      data-testid="live-leaderboard"
    >
      <div className="flex items-center gap-2 px-1 pb-1 border-b border-white/10">
        <Crown className="w-3.5 h-3.5 text-[#FFD93D]" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#FFD93D]">
          top gifters
        </span>
      </div>
      {entries.map((e, i) => {
        const style = RANK_STYLE[i] || null;
        const Icon = style?.Icon;
        return (
          <div
            key={e.user_id}
            className={`relative flex items-center gap-2 px-1.5 py-1 ${style ? "border " + style.ring + " " + style.glow : "border-l-2 border-white/20"} overflow-hidden`}
            data-testid={`live-leaderboard-rank-${i + 1}`}
          >
            {style && (
              <div
                className={`absolute inset-0 pointer-events-none bg-gradient-to-r ${style.grad}`}
              />
            )}
            <div className="relative flex items-center justify-center w-5 h-5 shrink-0">
              {Icon ? (
                <Icon className={`w-4 h-4 ${style.text}`} />
              ) : (
                <span className="text-[10px] font-mono text-white/60">#{i + 1}</span>
              )}
            </div>
            {e.picture ? (
              <img
                src={e.picture}
                alt=""
                className="relative w-6 h-6 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="relative w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] shrink-0">
                {(e.name || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="relative flex-1 min-w-0">
              <p className={`text-[11px] font-bold truncate ${style ? style.text : "text-white/80"}`}>
                {e.name}
              </p>
              <p className="text-[10px] font-mono text-white/50">
                {e.total_coins.toLocaleString()}c · {e.gift_count}g
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
